import { FC, useEffect, useState } from 'react';
import { IEntry, IProof } from 'src/ts/interfaces';
import {
  FormValues,
  getEffectiveProofResult,
  getRulingIcon,
  isUnverifiedOfferLetter
} from '../helpers/proofHelpers';
import {
  DocumentEditButton,
  DocumentInfo,
  DocumentItem,
  DocumentsList,
  DocumentStatus,
  DocumentStatusIcon,
  DocumentStatusText,
  DropdownSelectWrapper,
  EntryDropdownContainer,
  EntryDropdownLabel,
  ErrorValidationMessage,
  ReportPlatformFeedbackLink,
  RightPanel,
  RightPanelContent,
  SendReportButton,
  SendReportContainer,
  SubmissionReviewContainer,
  SubmissionReviewContent,
  SubmissionReviewDescription,
  SubmissionReviewHeader,
  SubmissionReviewTitle,
  SubmissionReviewTitleRow
} from '../styles';
import ModernSubmissionDropdown from './ModernSubmissionDropdown';
import ProofPreview from './ProofPreview';

interface SubmissionReviewPanelProps {
  entries: IEntry[];
  selectedEntryId: string;
  onEntryChange: (entryId: string) => void;
  localFormData: Record<string, FormValues>;
  onEditDocument: (entryId: string, proofId: string) => void;
  onSendReport: () => void;
  proofs: IProof[];
  assignedEntry: IEntry | null;
  entryStatuses: Record<string, 'PENDING' | 'CLEAN' | 'EDITED' | 'UNDETERMINED'>;
  checkAllEntriesRuled: () => boolean;
  onReportPlatformFeedback?: () => void;
}

interface DocumentRowProps {
  proof: IProof;
  localFormData: Record<string, FormValues>;
  selectedEntryId: string;
  onEditDocument: (entryId: string, proofId: string) => void;
  onMouseEnter: (proof: IProof, event: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onMouseMove: (event: React.MouseEvent) => void;
  rulingText: string;
}

const DocumentRow: FC<DocumentRowProps> = ({
  proof,
  localFormData,
  selectedEntryId,
  onEditDocument,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  rulingText
}) => {
  const editable = !isUnverifiedOfferLetter(proof);
  const filenameLabel = proof.short_id || proof.id;
  return (
    <DocumentItem data-document-item>
      <DocumentInfo>
        {editable ? (
          <span
            role="button"
            tabIndex={0}
            onMouseEnter={(e) => onMouseEnter(proof, e)}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onClick={() => onEditDocument(selectedEntryId, proof.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEditDocument(selectedEntryId, proof.id);
              }
            }}
          >
            {filenameLabel}
          </span>
        ) : (
          // Non-editable row: keep the element focusable for keyboard-only
          // analysts and announce the suppressed state to assistive tech.
          // No `role="button"` because there is no action — this is informational.
          <span
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label={`${filenameLabel} — unverified offer letter, no action required`}
            onMouseEnter={(e) => onMouseEnter(proof, e)}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
          >
            {filenameLabel}
          </span>
        )}
        <DocumentStatus>
          <DocumentStatusIcon src={getRulingIcon(proof, localFormData)} alt="Ruling status" />
          <DocumentStatusText id={filenameLabel}>{rulingText}</DocumentStatusText>
        </DocumentStatus>
      </DocumentInfo>
      {editable && (
        <DocumentEditButton onClick={() => onEditDocument(selectedEntryId, proof.id)}>
          Edit
        </DocumentEditButton>
      )}
    </DocumentItem>
  );
};

const SubmissionReviewPanel: FC<SubmissionReviewPanelProps> = ({
  entries,
  selectedEntryId,
  onEntryChange,
  localFormData,
  onEditDocument,
  onSendReport,
  proofs,
  assignedEntry,
  entryStatuses,
  checkAllEntriesRuled,
  onReportPlatformFeedback
}) => {
  const [validationError, setValidationError] = useState<string>('');
  const [hoveredProof, setHoveredProof] = useState<IProof | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const entryProofs = proofs.filter((proof) => proof.entry_id === selectedEntryId);

  // ITL-2690: per the product capability ticket, unverified offer letters
  // render under the AV Ruled section even though the backend keeps
  // `is_automatic_review=false` for them (kept off to avoid impacting
  // auto-rejection metrics and dashboards). The UI uses `isUnverifiedOfferLetter`
  // as a secondary routing key on top of the `is_automatic_review` flag.
  const avRuledProofs = entryProofs.filter(
    (proof) => proof.is_automatic_review || isUnverifiedOfferLetter(proof)
  );
  const manuallyRuledProofs = entryProofs.filter(
    (proof) => !proof.is_automatic_review && !isUnverifiedOfferLetter(proof)
  );

  useEffect(() => {
    entryProofs.forEach((proof) => {
      if (proof.thumb) {
        const img = new Image();
        img.src = proof.thumb;
      }
    });
  }, [entryProofs]);

  const formatEntryTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })} at ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  const getEntryResultIcon = (result: string) => {
    switch (result) {
      case 'CLEAN':
        return '/assets/images/clean.png';
      case 'EDITED':
        return '/assets/images/edited.png';
      case 'UNDETERMINED':
        return '/assets/images/insufficient.png';
      default:
        return '/assets/images/pending.svg';
    }
  };

  const getEntryRulingStatus = (entry: IEntry | undefined) => {
    if (!entry) return 'PENDING';

    if (entryStatuses[entry.id]) {
      return entryStatuses[entry.id];
    }

    if (entry.result && entry.result !== 'PENDING') {
      return entry.result;
    }

    return 'PENDING';
  };

  const getDocumentRulingText = (proof: IProof) => {
    const result = getEffectiveProofResult(proof, localFormData[proof.id]);

    if (!result || result === 'PENDING') return 'PENDING';

    switch (result) {
      case 'CLEAN':
        return 'CLEAN';
      case 'EDITED':
        return 'EDITED';
      case 'UNDETERMINED':
        return 'INSUFFICIENT';
      default:
        return 'PENDING';
    }
  };

  const handleSendReport = () => {
    if (checkAllEntriesRuled()) {
      setValidationError('');
      onSendReport();
    } else {
      setValidationError('A decision on all documents must be made before the report can be sent.');
    }
  };

  useEffect(() => {
    if (validationError && checkAllEntriesRuled()) {
      setValidationError('');
    }
  }, [localFormData, validationError, checkAllEntriesRuled]);

  const handleMouseEnter = (proof: IProof, event: React.MouseEvent) => {
    setHoveredProof(proof);
    setMousePosition({ x: 0, y: 0 });
  };

  const handleMouseLeave = () => {
    setHoveredProof(null);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (hoveredProof) {
      setMousePosition({ x: event.clientX + 10, y: event.clientY - 200 });
    }
  };

  const entryOptions = entries.map((entry) => {
    const entryProofCount = proofs.filter((proof) => proof.entry_id === entry.id).length;
    return {
      value: entry.id,
      label: formatEntryTimestamp(entry.submission_time),
      icon: getEntryResultIcon(getEntryRulingStatus(entry)),
      status: getEntryRulingStatus(entry),
      applicantName: entry.applicant?.full_name || 'Unknown',
      documentCount: entryProofCount
    };
  });

  return (
    <RightPanel>
      <RightPanelContent>
        <SubmissionReviewContainer>
          <SubmissionReviewHeader>
            <SubmissionReviewTitleRow>
              <SubmissionReviewTitle>Submission Review</SubmissionReviewTitle>
              {onReportPlatformFeedback && (
                <ReportPlatformFeedbackLink onClick={onReportPlatformFeedback}>
                  Report a bug
                </ReportPlatformFeedbackLink>
              )}
            </SubmissionReviewTitleRow>
            <SubmissionReviewDescription>
              The rulings on the following documents in this submission have been finalized, and the
              final report is ready to be sent. However, you may still edit each ruling prior to
              sending.
            </SubmissionReviewDescription>
          </SubmissionReviewHeader>

          <SubmissionReviewContent>
            <EntryDropdownContainer>
              <EntryDropdownLabel>Entry</EntryDropdownLabel>
              <DropdownSelectWrapper>
                <ModernSubmissionDropdown
                  id="fraud-entry-selector"
                  value={selectedEntryId}
                  onChange={onEntryChange}
                  options={entryOptions}
                  assignedEntryId={assignedEntry?.id}
                />
              </DropdownSelectWrapper>
            </EntryDropdownContainer>

            <div>
              <p>
                One or more documents within the submission were ruled{' '}
                {getEntryRulingStatus(entries.find((e) => e.id === selectedEntryId)) === 'CLEAN'
                  ? 'complete'
                  : 'pending'}
                .
              </p>

              {avRuledProofs.length > 0 && (
                <DocumentsList data-testid="av-ruled-section">
                  <h3>AV Ruled</h3>
                  {avRuledProofs.map((proof) => (
                    <DocumentRow
                      key={proof.id}
                      proof={proof}
                      localFormData={localFormData}
                      selectedEntryId={selectedEntryId}
                      onEditDocument={onEditDocument}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onMouseMove={handleMouseMove}
                      rulingText={getDocumentRulingText(proof)}
                    />
                  ))}
                </DocumentsList>
              )}

              {manuallyRuledProofs.length > 0 && (
                <DocumentsList data-testid="manually-ruled-section">
                  <h3>Manually Ruled</h3>
                  {manuallyRuledProofs.map((proof) => (
                    <DocumentRow
                      key={proof.id}
                      proof={proof}
                      localFormData={localFormData}
                      selectedEntryId={selectedEntryId}
                      onEditDocument={onEditDocument}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onMouseMove={handleMouseMove}
                      rulingText={getDocumentRulingText(proof)}
                    />
                  ))}
                </DocumentsList>
              )}
            </div>

            <SendReportContainer>
              <SendReportButton data-testid="review-send-report" onClick={handleSendReport}>
                Send report
                <img src="/assets/images/send-report.svg" alt="Send report" />
              </SendReportButton>
              {validationError && (
                <ErrorValidationMessage>{validationError}</ErrorValidationMessage>
              )}
            </SendReportContainer>
          </SubmissionReviewContent>
        </SubmissionReviewContainer>
      </RightPanelContent>

      {hoveredProof && (
        <ProofPreview
          proof={hoveredProof}
          applicantName={entries.find((e) => e.id === selectedEntryId)?.applicant?.full_name}
          isVisible={true}
          position={mousePosition}
          localFormData={localFormData}
        />
      )}
    </RightPanel>
  );
};

export default SubmissionReviewPanel;
